/**
 * @license
 * Copyright 2026 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import "#src/ui/three_point_registration.css";

import {
  extendTransformedBoundingBoxUpToRank,
  makeCoordinateSpace,
  makeDimensionNameUnique,
  makeSingletonDimTransformedBoundingBox,
  newDimensionId,
  type CoordinateSpace,
  type CoordinateSpaceTransform,
} from "#src/coordinate_transform.js";
import { initializeLayerFromSpec, ManagedUserLayer } from "#src/layer/index.js";
import { layerDataSourceSpecificationToJson } from "#src/layer/layer_data_source.js";
import { StatusMessage } from "#src/status.js";
import type {
  RegistrationPointPair,
  RegistrationResult,
} from "#src/three_point_registration.js";
import { estimateThreePointRegistrationWithCoordinateScales } from "#src/three_point_registration.js";
import {
  TrackableBoolean,
  TrackableBooleanCheckbox,
} from "#src/trackable_boolean.js";
import type { SidePanelManager } from "#src/ui/side_panel.js";
import { SidePanel } from "#src/ui/side_panel.js";
import type { SidePanelLocation } from "#src/ui/side_panel_location.js";
import {
  DEFAULT_SIDE_PANEL_LOCATION,
  TrackableSidePanelLocation,
} from "#src/ui/side_panel_location.js";
import { registerEventListener } from "#src/util/disposable.js";
import type { ActionEvent } from "#src/util/event_action_map.js";
import {
  EventActionMap,
  registerActionListener,
} from "#src/util/event_action_map.js";
import {
  emptyToUndefined,
  parseArray,
  parseFixedLengthArray,
  verifyBoolean,
  verifyFiniteFloat,
  verifyObject,
  verifyOptionalObjectProperty,
  verifyOptionalString,
} from "#src/util/json.js";
import * as matrix from "#src/util/matrix.js";
import { pickSiPrefix, scaleByExp10 } from "#src/util/si_units.js";
import { NullarySignal } from "#src/util/signal.js";
import type { Trackable } from "#src/util/trackable.js";
import type { Viewer } from "#src/viewer.js";
import { makeCopyButton } from "#src/widget/copy_button.js";

const CAPTURE_ACTION = "three-point-registration-capture";
const DISPLAY_SIGNIFICANT_FIGURES = 4;
const REQUIRED_REGISTRATION_POINT_COUNT = 3;
const MAX_REGISTRATION_POINT_COUNT = 6;

const DEFAULT_THREE_POINT_REGISTRATION_PANEL_LOCATION: SidePanelLocation = {
  ...DEFAULT_SIDE_PANEL_LOCATION,
  side: "right",
  row: 0,
  minSize: 260,
  visible: false,
};

type PointKind = "fixed" | "moving";
export type TransformCopyFormat = "csv" | "tsv" | "numpy" | "json" | "mango";
export type TransformCopyMatrixShape = "3x4" | "4x4";

const transformCopyFormats: TransformCopyFormat[] = [
  "numpy",
  "csv",
  "tsv",
  "json",
  "mango",
];
const transformCopyMatrixShapes: TransformCopyMatrixShape[] = ["4x4", "3x4"];

function getDefaultOutputLayerName(movingLayerName: string | undefined) {
  return movingLayerName === undefined
    ? undefined
    : `${movingLayerName}_aligned`;
}

/**
 * Name of the reverse-aligned layer, i.e. the copy of the fixed layer mapped into the transform
 * layer's frame.
 */
export function getReverseOutputLayerName(fixedLayerName: string | undefined) {
  return fixedLayerName === undefined ? undefined : `${fixedLayerName}_aligned`;
}

function verifyTransformCopyFormat(value: unknown): TransformCopyFormat {
  const format = verifyOptionalString(value) ?? "numpy";
  if (
    format === "csv" ||
    format === "tsv" ||
    format === "numpy" ||
    format === "json" ||
    format === "mango"
  ) {
    return format;
  }
  throw new Error(`Invalid transform copy format: ${JSON.stringify(format)}`);
}

function verifyTransformCopyMatrixShape(
  value: unknown,
): TransformCopyMatrixShape {
  const shape = verifyOptionalString(value) ?? "4x4";
  if (shape === "3x4" || shape === "4x4") {
    return shape;
  }
  throw new Error(`Invalid transform matrix shape: ${JSON.stringify(shape)}`);
}

export interface CapturedRegistrationPoint {
  coordinates: Float64Array;
}

function pointFromJson(obj: unknown): CapturedRegistrationPoint | undefined {
  if (obj == null) return undefined;
  return {
    coordinates: parseFixedLengthArray(
      new Float64Array(3),
      obj,
      verifyFiniteFloat,
    ),
  };
}

function pointToJson(point: CapturedRegistrationPoint | undefined) {
  return point === undefined ? null : Array.from(point.coordinates);
}

function makeEmptyRegistrationPoints() {
  return Array<CapturedRegistrationPoint | undefined>(
    MAX_REGISTRATION_POINT_COUNT,
  ).fill(undefined);
}

function pointsToJson(
  points: readonly (CapturedRegistrationPoint | undefined)[],
) {
  let length = points.length;
  while (
    length > REQUIRED_REGISTRATION_POINT_COUNT &&
    points[length - 1] === undefined
  ) {
    --length;
  }
  return points.slice(0, length).map(pointToJson);
}

export class ThreePointRegistrationPanelState implements Trackable {
  changed = new NullarySignal();
  location = new TrackableSidePanelLocation(
    DEFAULT_THREE_POINT_REGISTRATION_PANEL_LOCATION,
  );
  allowScale = new TrackableBoolean(false, false);
  fixedLayerName: string | undefined;
  movingLayerName: string | undefined;
  outputLayerName: string | undefined;
  outputLayerNameWasEdited = false;
  copyFormat: TransformCopyFormat = "numpy";
  copyMatrixShape: TransformCopyMatrixShape = "4x4";
  fixedPoints = makeEmptyRegistrationPoints();
  movingPoints = makeEmptyRegistrationPoints();

  constructor() {
    this.location.changed.add(this.changed.dispatch);
    this.allowScale.changed.add(this.changed.dispatch);
  }

  setLayerName(kind: PointKind, name: string | undefined) {
    if (kind === "fixed") {
      if (this.fixedLayerName === name) return;
      this.fixedLayerName = name;
    } else {
      const previousDefaultOutputLayerName = getDefaultOutputLayerName(
        this.movingLayerName,
      );
      if (this.movingLayerName === name) return;
      this.movingLayerName = name;
      if (
        !this.outputLayerNameWasEdited ||
        this.outputLayerName === undefined ||
        this.outputLayerName === previousDefaultOutputLayerName
      ) {
        this.outputLayerName = getDefaultOutputLayerName(name);
        this.outputLayerNameWasEdited = false;
      }
    }
    this.changed.dispatch();
  }

  setOutputLayerName(name: string | undefined, edited = true) {
    const normalizedName = name === "" ? undefined : name;
    if (
      this.outputLayerName === normalizedName &&
      this.outputLayerNameWasEdited === edited
    ) {
      return;
    }
    this.outputLayerName = normalizedName;
    this.outputLayerNameWasEdited = edited;
    this.changed.dispatch();
  }

  setCopyFormat(format: TransformCopyFormat) {
    if (this.copyFormat === format) return;
    this.copyFormat = format;
    this.changed.dispatch();
  }

  setCopyMatrixShape(shape: TransformCopyMatrixShape) {
    if (this.copyMatrixShape === shape) return;
    this.copyMatrixShape = shape;
    this.changed.dispatch();
  }

  setPoint(
    kind: PointKind,
    index: number,
    point: CapturedRegistrationPoint | undefined,
  ) {
    if (index < 0 || index >= MAX_REGISTRATION_POINT_COUNT) return;
    const points = kind === "fixed" ? this.fixedPoints : this.movingPoints;
    points[index] = point;
    this.changed.dispatch();
  }

  isPointPairComplete(index: number) {
    return (
      this.fixedPoints[index] !== undefined &&
      this.movingPoints[index] !== undefined
    );
  }

  isPointIndexEnabled(index: number) {
    if (index < REQUIRED_REGISTRATION_POINT_COUNT) return true;
    if (
      this.fixedPoints[index] !== undefined ||
      this.movingPoints[index] !== undefined
    ) {
      return true;
    }
    for (let i = 0; i < index; ++i) {
      if (!this.isPointPairComplete(i)) return false;
    }
    return true;
  }

  reset() {
    this.location.reset();
    this.allowScale.value = false;
    this.fixedLayerName = undefined;
    this.movingLayerName = undefined;
    this.outputLayerName = undefined;
    this.outputLayerNameWasEdited = false;
    this.copyFormat = "numpy";
    this.copyMatrixShape = "4x4";
    this.fixedPoints.fill(undefined);
    this.movingPoints.fill(undefined);
    this.changed.dispatch();
  }

  restoreState(obj: unknown) {
    if (obj === undefined) return;
    verifyObject(obj);
    this.location.restoreState(
      obj,
      DEFAULT_THREE_POINT_REGISTRATION_PANEL_LOCATION,
    );
    this.fixedLayerName = verifyOptionalObjectProperty(
      obj,
      "fixedLayer",
      verifyOptionalString,
    );
    this.movingLayerName = verifyOptionalObjectProperty(
      obj,
      "movingLayer",
      verifyOptionalString,
    );
    this.outputLayerName = verifyOptionalObjectProperty(
      obj,
      "outputLayer",
      verifyOptionalString,
      getDefaultOutputLayerName(this.movingLayerName),
    );
    this.outputLayerNameWasEdited =
      this.outputLayerName !== getDefaultOutputLayerName(this.movingLayerName);
    this.copyFormat = verifyOptionalObjectProperty(
      obj,
      "copyFormat",
      verifyTransformCopyFormat,
      "numpy",
    );
    this.copyMatrixShape = verifyOptionalObjectProperty(
      obj,
      "copyMatrixShape",
      verifyTransformCopyMatrixShape,
      "4x4",
    );
    this.allowScale.restoreState(
      verifyOptionalObjectProperty(obj, "allowScale", verifyBoolean),
    );
    this.fixedPoints = verifyOptionalObjectProperty(
      obj,
      "fixedPoints",
      (pointsObj) => parseArray(pointsObj, pointFromJson),
      [],
    ).slice(0, MAX_REGISTRATION_POINT_COUNT);
    this.movingPoints = verifyOptionalObjectProperty(
      obj,
      "movingPoints",
      (pointsObj) => parseArray(pointsObj, pointFromJson),
      [],
    ).slice(0, MAX_REGISTRATION_POINT_COUNT);
    while (this.fixedPoints.length < MAX_REGISTRATION_POINT_COUNT) {
      this.fixedPoints.push(undefined);
    }
    while (this.movingPoints.length < MAX_REGISTRATION_POINT_COUNT) {
      this.movingPoints.push(undefined);
    }
    this.changed.dispatch();
  }

  toJSON() {
    const obj: any = emptyToUndefined(
      this.location.toJSON(DEFAULT_THREE_POINT_REGISTRATION_PANEL_LOCATION),
    );
    const result = obj ?? {};
    if (this.fixedLayerName !== undefined)
      result.fixedLayer = this.fixedLayerName;
    if (this.movingLayerName !== undefined)
      result.movingLayer = this.movingLayerName;
    if (this.outputLayerName !== undefined)
      result.outputLayer = this.outputLayerName;
    if (this.copyFormat !== "numpy") result.copyFormat = this.copyFormat;
    if (this.copyMatrixShape !== "4x4")
      result.copyMatrixShape = this.copyMatrixShape;
    if (this.allowScale.value) result.allowScale = true;
    if (this.fixedPoints.some((point) => point !== undefined)) {
      result.fixedPoints = pointsToJson(this.fixedPoints);
    }
    if (this.movingPoints.some((point) => point !== undefined)) {
      result.movingPoints = pointsToJson(this.movingPoints);
    }
    return emptyToUndefined(result);
  }
}

interface ComputedRegistration {
  movingLayer: ManagedUserLayer;
  fixedLayer: ManagedUserLayer;
  layout: RegistrationLayout;
  pairs: RegistrationPointPair[];
  /** Maps the transform layer into the fixed layer's frame. */
  result: RegistrationResult;
  /** Maps the fixed layer into the transform layer's frame. */
  reverseResult: RegistrationResult;
}

interface LayerTransformDifference {
  transformLayer: ManagedUserLayer;
  alignedLayer: ManagedUserLayer;
  transform: Float64Array;
  rank: number;
  copyLayout: TransformCopyLayout;
}

export interface PhysicalDimension {
  scale: number;
  unit: string;
}

interface RegistrationLayout {
  fixedLayer: ManagedUserLayer;
  dimensionNames: [string, string, string];
  fixedOutputDimensionIndices: [
    number | undefined,
    number | undefined,
    number | undefined,
  ];
  movingOutputDimensionIndices: [
    number | undefined,
    number | undefined,
    number | undefined,
  ];
  dimensions: [PhysicalDimension, PhysicalDimension, PhysicalDimension];
}

const CANONICAL_SPATIAL_DIMENSIONS = ["x", "y", "z"] as const;

function getPresentDimensionCount(
  outputDimensionNames: readonly string[],
  registrationDimensionNames: readonly string[],
) {
  return registrationDimensionNames.reduce(
    (count, name) => count + Number(outputDimensionNames.includes(name)),
    0,
  );
}

/** Resolves the common 3D frame, permitting one singleton axis per layer. */
export function getThreePointRegistrationDimensionNames(
  fixedOutputDimensionNames: readonly string[],
  movingOutputDimensionNames: readonly string[],
  displayedDimensionNames: readonly string[],
): [string, string, string] {
  const canonicalFixedCount = getPresentDimensionCount(
    fixedOutputDimensionNames,
    CANONICAL_SPATIAL_DIMENSIONS,
  );
  const canonicalMovingCount = getPresentDimensionCount(
    movingOutputDimensionNames,
    CANONICAL_SPATIAL_DIMENSIONS,
  );
  if (
    canonicalFixedCount >= 2 &&
    canonicalMovingCount >= 2 &&
    canonicalFixedCount <= 3 &&
    canonicalMovingCount <= 3
  ) {
    return [...CANONICAL_SPATIAL_DIMENSIONS];
  }
  if (displayedDimensionNames.length === 3) {
    const result = displayedDimensionNames as [string, string, string];
    const fixedCount = getPresentDimensionCount(
      fixedOutputDimensionNames,
      result,
    );
    const movingCount = getPresentDimensionCount(
      movingOutputDimensionNames,
      result,
    );
    if (fixedCount >= 2 && movingCount >= 2) return [...result];
  }
  throw new Error(
    "Registration requires each layer to provide at least two dimensions in an unambiguous three-dimensional spatial frame.",
  );
}

function getOutputDimensionIndices(
  transform: CoordinateSpaceTransform,
  dimensionNames: readonly string[],
): [number | undefined, number | undefined, number | undefined] {
  return dimensionNames.map((name) => {
    const index = transform.outputSpace.names.indexOf(name);
    return index === -1 ? undefined : index;
  }) as [number | undefined, number | undefined, number | undefined];
}

function extendCoordinateSpace(
  space: CoordinateSpace,
  oldTransformRank: number,
  addedDimensions: readonly { name: string; dimension: PhysicalDimension }[],
  input: boolean,
) {
  const newRank = oldTransformRank + addedDimensions.length;
  const names = space.names.slice();
  const units = space.units.slice();
  const scales = Array.from(space.scales);
  const ids = space.ids.slice();
  const timestamps = space.timestamps.slice();
  const coordinateArrays = space.coordinateArrays.slice();
  while (names.length < oldTransformRank) {
    names.push(makeDimensionNameUnique(`${names.length}`, names));
    units.push("");
    scales.push(1);
    ids.push(newDimensionId());
    timestamps.push(Number.NEGATIVE_INFINITY);
    coordinateArrays.push(undefined);
  }
  for (const { name, dimension } of addedDimensions) {
    names.push(input ? makeDimensionNameUnique(name, names) : name);
    units.push(dimension.unit);
    scales.push(dimension.scale);
    ids.push(newDimensionId());
    timestamps.push(Number.NEGATIVE_INFINITY);
    coordinateArrays.push(undefined);
  }
  const boundingBoxes = space.boundingBoxes.map((box) =>
    extendTransformedBoundingBoxUpToRank(box, space.rank, newRank),
  );
  for (let i = space.rank; i < newRank; ++i) {
    boundingBoxes.push(makeSingletonDimTransformedBoundingBox(newRank, i));
  }
  return makeCoordinateSpace({
    valid: space.valid,
    names,
    units,
    scales: Float64Array.from(scales),
    ids,
    timestamps,
    boundingBoxes,
    coordinateArrays,
  });
}

/** Adds absent registration axes as synthetic singleton dimensions. */
export function expandTransformForThreePointRegistration(
  transform: CoordinateSpaceTransform,
  dimensionNames: readonly string[],
  dimensions: readonly PhysicalDimension[],
): CoordinateSpaceTransform {
  const addedDimensions = dimensionNames.flatMap((name, i) =>
    transform.outputSpace.names.includes(name)
      ? []
      : [{ name, dimension: dimensions[i] }],
  );
  if (addedDimensions.length === 0) return transform;
  const oldRank = transform.rank;
  const newRank = oldRank + addedDimensions.length;
  const oldStride = oldRank + 1;
  const newStride = newRank + 1;
  const expanded = new Float64Array(newStride * newStride);
  expanded[newRank * newStride + newRank] = 1;
  for (let col = 0; col < oldRank; ++col) {
    for (let row = 0; row < oldRank; ++row) {
      expanded[col * newStride + row] =
        transform.transform[col * oldStride + row];
    }
  }
  for (let row = 0; row < oldRank; ++row) {
    expanded[newRank * newStride + row] =
      transform.transform[oldRank * oldStride + row];
  }
  for (let i = oldRank; i < newRank; ++i) {
    expanded[i * newStride + i] = 1;
  }
  return {
    rank: newRank,
    sourceRank: transform.sourceRank,
    transform: expanded,
    inputSpace: extendCoordinateSpace(
      transform.inputSpace,
      oldRank,
      addedDimensions,
      true,
    ),
    outputSpace: extendCoordinateSpace(
      transform.outputSpace,
      oldRank,
      addedDimensions,
      false,
    ),
  };
}

function makeRegistrationDeltaTransform(
  transform: CoordinateSpaceTransform,
  dimensionNames: readonly string[],
  result: RegistrationResult,
) {
  const rank = transform.rank;
  const stride = rank + 1;
  const delta = matrix.createIdentity(Float64Array, stride);
  const outputIndices = dimensionNames.map((name) => {
    const index = transform.outputSpace.names.indexOf(name);
    if (index === -1) throw new Error(`Missing spatial dimension ${name}.`);
    return index;
  });
  for (let row = 0; row < 3; ++row) {
    const outputRow = outputIndices[row];
    for (let col = 0; col < 3; ++col) {
      delta[outputIndices[col] * stride + outputRow] =
        result.transform[col * 4 + row];
    }
    delta[rank * stride + outputRow] = result.transform[12 + row];
  }
  return delta;
}

/** Expands a transform as needed and applies a 3D registration in output space. */
export function applyThreePointRegistrationResult(
  transform: CoordinateSpaceTransform,
  dimensionNames: readonly string[],
  dimensions: readonly PhysicalDimension[],
  result: RegistrationResult,
): CoordinateSpaceTransform {
  const expandedTransform = expandTransformForThreePointRegistration(
    transform,
    dimensionNames,
    dimensions,
  );
  const physicalTransform = result.physicalTransform;
  if (physicalTransform !== undefined) {
    const rank = expandedTransform.rank;
    const stride = rank + 1;
    const outputIndices = dimensionNames.map((name) => {
      const index = expandedTransform.outputSpace.names.indexOf(name);
      if (index === -1) throw new Error(`Missing spatial dimension ${name}.`);
      return index;
    });
    const oldTransform = expandedTransform.transform;
    const transform = new Float64Array(oldTransform);
    for (let row = 0; row < 3; ++row) {
      const outputRow = outputIndices[row];
      for (let col = 0; col < rank; ++col) {
        let value = 0;
        for (let k = 0; k < 3; ++k) {
          value +=
            physicalTransform[k * 4 + row] *
            oldTransform[col * stride + outputIndices[k]];
        }
        transform[col * stride + outputRow] = value;
      }
      let physicalTranslation = physicalTransform[12 + row];
      for (let k = 0; k < 3; ++k) {
        const oldOutputRow = outputIndices[k];
        physicalTranslation +=
          physicalTransform[k * 4 + row] *
          oldTransform[rank * stride + oldOutputRow] *
          expandedTransform.outputSpace.scales[oldOutputRow];
      }
      transform[rank * stride + outputRow] =
        physicalTranslation / expandedTransform.outputSpace.scales[outputRow];
    }
    return { ...expandedTransform, transform };
  }
  const stride = expandedTransform.rank + 1;
  const deltaTransform = makeRegistrationDeltaTransform(
    expandedTransform,
    dimensionNames,
    result,
  );
  return {
    ...expandedTransform,
    transform: multiplySquare(
      deltaTransform,
      expandedTransform.transform,
      stride,
    ),
  };
}

/** Computes the scale-aware physical delta from original to aligned. */
export function getThreePointRegistrationTransformDifference(
  originalTransform: CoordinateSpaceTransform,
  alignedTransform: CoordinateSpaceTransform,
  dimensionNames: readonly string[],
  dimensions: readonly PhysicalDimension[],
) {
  const expandedOriginal = expandTransformForThreePointRegistration(
    originalTransform,
    dimensionNames,
    dimensions,
  );
  const rank = expandedOriginal.rank;
  if (alignedTransform.rank !== rank) {
    throw new Error("Transform and aligned layer ranks do not match.");
  }
  const stride = rank + 1;
  const inverseOriginal = new Float64Array(expandedOriginal.transform);
  const determinant = matrix.inverseInplace(inverseOriginal, stride, stride);
  if (determinant === 0) {
    throw new Error("Transform layer transform is singular.");
  }
  const transform = multiplySquare(
    alignedTransform.transform,
    inverseOriginal,
    stride,
  );
  for (const name of dimensionNames) {
    const row = alignedTransform.outputSpace.names.indexOf(name);
    if (row === -1) throw new Error(`Missing spatial dimension ${name}.`);
    let physicalTranslation =
      alignedTransform.transform[rank * stride + row] *
      alignedTransform.outputSpace.scales[row];
    for (const inputName of dimensionNames) {
      const col = expandedOriginal.outputSpace.names.indexOf(inputName);
      if (col === -1) {
        throw new Error(`Missing spatial dimension ${inputName}.`);
      }
      physicalTranslation -=
        transform[col * stride + row] *
        expandedOriginal.transform[rank * stride + col] *
        expandedOriginal.outputSpace.scales[col];
    }
    transform[rank * stride + row] =
      physicalTranslation / alignedTransform.outputSpace.scales[row];
  }
  return { rank, transform };
}

function getMouseCoordinates(
  viewer: Viewer,
  dimNames: readonly string[],
  missing: "fail" | "skip",
): Float64Array {
  const { mouseState } = viewer;
  if (!mouseState.updateUnconditionally()) {
    throw new Error("No data position is available at the click location.");
  }
  const mouseNames = mouseState.coordinateSpace.names;
  const result = new Float64Array(dimNames.length);
  for (let i = 0; i < dimNames.length; ++i) {
    const mouseDim = mouseNames.indexOf(dimNames[i]);
    if (mouseDim === -1) {
      if (missing === "fail") {
        throw new Error(
          `Clicked position is missing dimension ${dimNames[i]}.`,
        );
      }
      continue;
    }
    result[i] = mouseState.unsnappedPosition[mouseDim];
  }
  return result;
}

function getFirstLoadedTransform(layer: ManagedUserLayer) {
  const userLayer = layer.layer;
  if (userLayer === null) return undefined;
  for (const dataSource of userLayer.dataSources) {
    const loadState = dataSource.loadState;
    if (loadState === undefined || loadState.error !== undefined) continue;
    return loadState.transform.value;
  }
  return undefined;
}

function multiplySquare(
  a: Float64Array,
  b: Float64Array,
  stride: number,
): Float64Array {
  return matrix.multiply(
    new Float64Array(stride * stride),
    stride,
    a,
    stride,
    b,
    stride,
    stride,
    stride,
    stride,
  );
}

function getUnitLabel(dimensions: readonly PhysicalDimension[]) {
  const normalizedUnits = dimensions.map(({ unit }) => unit || "unitless");
  return normalizedUnits.every((unit) => unit === normalizedUnits[0])
    ? normalizedUnits[0]
    : "mixed units";
}

function formatDisplayNumber(value: number) {
  if (!Number.isFinite(value)) return `${value}`;
  const formatted = value.toPrecision(DISPLAY_SIGNIFICANT_FIGURES);
  const eIndex = formatted.indexOf("e");
  const mantissa = eIndex === -1 ? formatted : formatted.substring(0, eIndex);
  const exponent = eIndex === -1 ? "" : formatted.substring(eIndex);
  if (!mantissa.includes(".")) return mantissa + exponent;
  return mantissa.replace(/0+$/, "").replace(/\.$/, "") + exponent;
}

function formatScaleWithUnitForDisplay(
  value: number,
  unit: string,
  { elide1 = true } = {},
) {
  let adjustedValue = value;
  let prefix = "";
  if (unit !== "" && value !== 0) {
    const siPrefix = pickSiPrefix(Math.abs(value));
    prefix = siPrefix.prefix;
    adjustedValue = scaleByExp10(value, -siPrefix.exponent);
  }
  if (elide1 && adjustedValue === 1) return `${prefix}${unit}`;
  return `${formatDisplayNumber(adjustedValue)}${prefix}${unit}`;
}

function formatPhysicalValue(value: number, dimension: PhysicalDimension) {
  return formatScaleWithUnitForDisplay(
    value * dimension.scale,
    dimension.unit,
    { elide1: false },
  );
}

function splitFormattedPhysicalValue(value: string) {
  const match = value.match(/^(.+?)([^0-9.+\-eE].*)$/);
  return match === null
    ? { value, unitSuffix: "" }
    : { value: match[1], unitSuffix: match[2] };
}

function formatDistance(
  value: number | undefined,
  unit: string,
  mixedUnits = false,
) {
  if (value === undefined) return "-";
  if (mixedUnits) return `${formatDisplayNumber(value)} mixed units`;
  return formatScaleWithUnitForDisplay(value, unit);
}

function formatDistanceList(
  values: readonly number[],
  unit: string,
  mixedUnits = false,
) {
  if (mixedUnits) {
    return `${values.map(formatDisplayNumber).join(", ")} mixed units`;
  }
  const formattedValues = values.map((value) =>
    splitFormattedPhysicalValue(formatScaleWithUnitForDisplay(value, unit)),
  );
  const unitSuffix = formattedValues[0]?.unitSuffix ?? "";
  if (
    unitSuffix !== "" &&
    formattedValues.every((formatted) => formatted.unitSuffix === unitSuffix)
  ) {
    return `${formattedValues.map(({ value }) => value).join(", ")} ${unitSuffix}`;
  }
  return values
    .map((value) => formatScaleWithUnitForDisplay(value, unit))
    .join(", ");
}

function distanceBetween(
  a: Float64Array | undefined,
  b: Float64Array | undefined,
  dimensions: readonly PhysicalDimension[],
) {
  if (a === undefined || b === undefined) return undefined;
  if (getUnitLabel(dimensions) === "mixed units") return undefined;
  let squaredDistance = 0;
  for (let i = 0; i < 3; ++i) {
    const difference = (a[i] - b[i]) * dimensions[i].scale;
    squaredDistance += difference * difference;
  }
  return Math.sqrt(squaredDistance);
}

function getPhysicalRegistrationErrors(
  result: RegistrationResult,
  pairs: readonly { moving: Float64Array; fixed: Float64Array }[],
  dimensions: readonly PhysicalDimension[],
) {
  const residuals = new Float64Array(pairs.length);
  let squaredSum = 0;
  let maxError = 0;
  for (let i = 0; i < pairs.length; ++i) {
    const { moving, fixed } = pairs[i];
    let squaredDistance = 0;
    for (let row = 0; row < 3; ++row) {
      const predicted =
        result.transform[row] * moving[0] +
        result.transform[4 + row] * moving[1] +
        result.transform[8 + row] * moving[2] +
        result.transform[12 + row];
      const difference = (predicted - fixed[row]) * dimensions[row].scale;
      squaredDistance += difference * difference;
    }
    const residual = Math.sqrt(squaredDistance);
    residuals[i] = residual;
    squaredSum += squaredDistance;
    maxError = Math.max(maxError, residual);
  }
  return {
    residuals,
    rmsError: Math.sqrt(squaredSum / pairs.length),
    maxError,
  };
}

function formatPhysicalCoordinates(
  point: Float64Array | undefined,
  dimensions: readonly PhysicalDimension[],
) {
  if (point === undefined) return "-";
  const unitLabel = getUnitLabel(dimensions);
  if (unitLabel !== "mixed units") {
    const formattedValues = Array.from(point, (x, i) =>
      splitFormattedPhysicalValue(formatPhysicalValue(x, dimensions[i])),
    );
    const unitSuffix = formattedValues[0].unitSuffix;
    if (
      unitSuffix !== "" &&
      formattedValues.every((formatted) => formatted.unitSuffix === unitSuffix)
    ) {
      return `${formattedValues.map(({ value }) => value).join(", ")} ${unitSuffix}`;
    }
  }
  return Array.from(point, (x, i) =>
    formatPhysicalValue(x, dimensions[i]),
  ).join(", ");
}

export interface TransformCopyLayout {
  /** Indices of the x, y, and z registration dimensions in the transform. */
  dimensionIndices: readonly [number, number, number];
  /** Coordinate-to-physical scales, required for Mango export. */
  dimensions?: readonly [
    PhysicalDimension,
    PhysicalDimension,
    PhysicalDimension,
  ];
}

function canonicalizeTransformForCopy(
  transform: Float64Array,
  rank: number,
  layout: TransformCopyLayout | undefined,
) {
  const indices = layout?.dimensionIndices ?? [0, 1, 2];
  if (
    indices.some(
      (index) => !Number.isInteger(index) || index < 0 || index >= rank,
    ) ||
    new Set(indices).size !== 3
  ) {
    throw new Error(
      "Transform copy requires three distinct spatial dimensions.",
    );
  }
  const stride = rank + 1;
  const canonical = matrix.createIdentity(Float64Array, 4);
  for (let row = 0; row < 3; ++row) {
    for (let col = 0; col < 3; ++col) {
      canonical[col * 4 + row] =
        transform[indices[col] * stride + indices[row]];
    }
    canonical[12 + row] = transform[rank * stride + indices[row]];
  }
  return canonical;
}

function convertCanonicalTransformToMangoMillimetres(
  transform: Float64Array,
  dimensions:
    | readonly [PhysicalDimension, PhysicalDimension, PhysicalDimension]
    | undefined,
) {
  if (dimensions === undefined) return transform;
  if (!dimensions.every(({ unit }) => unit === "m")) {
    throw new Error(
      "Mango transform copy requires spatial dimensions in metres.",
    );
  }
  if (!dimensions.every(({ scale }) => scale > 0 && Number.isFinite(scale))) {
    throw new Error(
      "Mango transform coordinate scales must be finite and positive.",
    );
  }
  const result = new Float64Array(transform);
  for (let row = 0; row < 3; ++row) {
    const rowScale = dimensions[row].scale;
    result[12 + row] *= rowScale * 1000;
  }
  return result;
}

function matrixToCopyRows(
  transform: Float64Array,
  shape: TransformCopyMatrixShape,
) {
  const rows: number[][] = [];
  const numRows = shape === "4x4" ? 4 : 3;
  for (let row = 0; row < numRows; ++row) {
    const values: number[] = [];
    for (let col = 0; col < 4; ++col) {
      if (row < 3) {
        values[col] = col < 3 ? transform[col * 4 + row] : transform[12 + row];
      } else {
        values[col] = col === 3 ? 1 : 0;
      }
    }
    rows[row] = values;
  }
  return rows;
}

type Matrix3 = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

function determinant3(m: Matrix3) {
  return (
    m[0] * (m[4] * m[8] - m[5] * m[7]) -
    m[1] * (m[3] * m[8] - m[5] * m[6]) +
    m[2] * (m[3] * m[7] - m[4] * m[6])
  );
}

function invert3(m: Matrix3): Matrix3 | undefined {
  const det = determinant3(m);
  if (!Number.isFinite(det) || Math.abs(det) <= 1e-15) return undefined;
  const invDet = 1 / det;
  return [
    (m[4] * m[8] - m[5] * m[7]) * invDet,
    (m[2] * m[7] - m[1] * m[8]) * invDet,
    (m[1] * m[5] - m[2] * m[4]) * invDet,
    (m[5] * m[6] - m[3] * m[8]) * invDet,
    (m[0] * m[8] - m[2] * m[6]) * invDet,
    (m[2] * m[3] - m[0] * m[5]) * invDet,
    (m[3] * m[7] - m[4] * m[6]) * invDet,
    (m[1] * m[6] - m[0] * m[7]) * invDet,
    (m[0] * m[4] - m[1] * m[3]) * invDet,
  ];
}

function transpose3(m: Matrix3): Matrix3 {
  return [m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]];
}

function average3(a: Matrix3, b: Matrix3): Matrix3 {
  return [
    0.5 * (a[0] + b[0]),
    0.5 * (a[1] + b[1]),
    0.5 * (a[2] + b[2]),
    0.5 * (a[3] + b[3]),
    0.5 * (a[4] + b[4]),
    0.5 * (a[5] + b[5]),
    0.5 * (a[6] + b[6]),
    0.5 * (a[7] + b[7]),
    0.5 * (a[8] + b[8]),
  ];
}

function maxAbsDifference3(a: Matrix3, b: Matrix3) {
  let result = 0;
  for (let i = 0; i < 9; ++i) {
    result = Math.max(result, Math.abs(a[i] - b[i]));
  }
  return result;
}

function closestRotation3(a: Matrix3): Matrix3 {
  let rotation = a;
  for (let i = 0; i < 30; ++i) {
    const inverse = invert3(rotation);
    if (inverse === undefined) {
      throw new Error("Mango transform requires a nonsingular 3x3 block.");
    }
    const next = average3(rotation, transpose3(inverse));
    if (!next.every(Number.isFinite)) {
      throw new Error("Mango transform decomposition failed.");
    }
    if (maxAbsDifference3(next, rotation) < 1e-12) {
      rotation = next;
      break;
    }
    rotation = next;
  }
  const det = determinant3(rotation);
  if (!(det > 0) || !Number.isFinite(det)) {
    throw new Error("Mango transform requires a proper rotation.");
  }
  const detScale = Math.cbrt(det);
  return rotation.map((value) => value / detScale) as Matrix3;
}

function mangoRotationAnglesXyz(r: Matrix3) {
  const rx = Math.atan2(r[5], r[8]);
  const c1 = Math.sqrt(r[0] * r[0] + r[1] * r[1]);
  const ry = Math.atan2(r[2], c1);
  const s0 = Math.sin(rx);
  const c0 = Math.cos(rx);
  const rz = Math.atan2(s0 * r[6] - c0 * r[3], c0 * r[4] - s0 * r[7]);
  return [rx, ry, rz];
}

function formatMangoNumber(value: number) {
  if (!Number.isFinite(value)) {
    throw new Error("Mango transform contains a non-finite value.");
  }
  return value.toExponential(10).replace(/e([+-])(\d)$/, "e$10$2");
}

function formatMangoItems(values: readonly number[], indent: string) {
  return values
    .map((value) => `${indent}<item>${formatMangoNumber(value)}</item>`)
    .join("\n");
}

export function formatMangoTransformForCopy(
  transform: Float64Array,
  rank: number,
  layout?: TransformCopyLayout,
) {
  if (rank < 3) {
    throw new Error("Mango transform copy requires at least three dimensions.");
  }
  const canonicalTransform = convertCanonicalTransformToMangoMillimetres(
    canonicalizeTransformForCopy(transform, rank, layout),
    layout?.dimensions,
  );
  const stride = 4;
  const affine: Matrix3 = [
    canonicalTransform[0 * stride + 0],
    canonicalTransform[1 * stride + 0],
    canonicalTransform[2 * stride + 0],
    canonicalTransform[0 * stride + 1],
    canonicalTransform[1 * stride + 1],
    canonicalTransform[2 * stride + 1],
    canonicalTransform[0 * stride + 2],
    canonicalTransform[1 * stride + 2],
    canonicalTransform[2 * stride + 2],
  ];
  const det = determinant3(affine);
  if (!(det > 0) || !Number.isFinite(det)) {
    throw new Error("Mango transform requires a finite positive determinant.");
  }
  const rotation = closestRotation3(affine);
  const scale =
    (rotation[0] * affine[0] +
      rotation[3] * affine[3] +
      rotation[6] * affine[6] +
      rotation[1] * affine[1] +
      rotation[4] * affine[4] +
      rotation[7] * affine[7] +
      rotation[2] * affine[2] +
      rotation[5] * affine[5] +
      rotation[8] * affine[8]) /
    3;
  if (!(scale > 0) || !Number.isFinite(scale)) {
    throw new Error("Mango transform requires a finite positive scale.");
  }
  const parameters = [
    canonicalTransform[12],
    canonicalTransform[13],
    canonicalTransform[14],
    ...mangoRotationAnglesXyz(rotation),
    scale,
  ];
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes" ?>
<!DOCTYPE boost_serialization>
<boost_serialization signature="serialization::archive" version="20">
  <transform_description>PrmVecSerialized_RegEuclidSimilarityTransform_double_3_scale-rotate-translate</transform_description>
  <units class_id="0" tracking_level="0" version="0">
    <length>millimetre</length>
    <angle>radians</angle>
  </units>
  <transform class_id="1" tracking_level="0" version="0">
    <parameterDescription>[tx,ty,tz,rx,ry,rz,s]</parameterDescription>
    <parameters>
${formatMangoItems(parameters, "      ")}
    </parameters>
    <centre_coordinate>
${formatMangoItems([0, 0, 0], "      ")}
    </centre_coordinate>
  </transform>
  <transform_description>terminate</transform_description>
</boost_serialization>`;
}

export function formatAffineTransformForCopy(
  transform: Float64Array,
  rank: number,
  format: TransformCopyFormat,
  shape: TransformCopyMatrixShape = "4x4",
  layout?: TransformCopyLayout,
) {
  if (format === "mango") {
    return formatMangoTransformForCopy(transform, rank, layout);
  }
  const rows = matrixToCopyRows(
    canonicalizeTransformForCopy(transform, rank, layout),
    shape,
  );
  const formatNumber = (value: number) => `${value}`;
  if (format === "csv" || format === "tsv") {
    const separator = format === "csv" ? "," : "\t";
    return rows
      .map((row) => row.map((value) => formatNumber(value)).join(separator))
      .join("\n");
  }
  if (format === "json") {
    return JSON.stringify(rows, undefined, 2);
  }
  return `[${rows
    .map((row) => `[${row.map((value) => formatNumber(value)).join(", ")}]`)
    .join(",\n ")}]`;
}

function getLayerByName(viewer: Viewer, name: string | undefined) {
  if (name === undefined) return undefined;
  return viewer.layerManager.getLayerByName(name);
}

export class ThreePointRegistrationPanel extends SidePanel {
  private body = document.createElement("div");
  private fixedSelect = document.createElement("select");
  private movingSelect = document.createElement("select");
  private outputLayerNameInput = document.createElement("input");
  private copyFormatSelect = document.createElement("select");
  private copyMatrixShapeSelect = document.createElement("select");

  private pointCaptureButtons: Record<PointKind, HTMLButtonElement[]> = {
    fixed: [],
    moving: [],
  };
  private pointDetailsTable = document.createElement("div");
  private messageElement = document.createElement("div");
  private errorTable = document.createElement("div");
  private alignButton = document.createElement("button");
  private copyButton = makeCopyButton({
    title: "Copy transform from transform layer to aligned layer",
    onClick: () => this.copyTransform(),
  });
  private activeCapture:
    | {
        kind: PointKind;
        index: number;
        disposers: (() => void)[];
      }
    | undefined;

  constructor(
    sidePanelManager: SidePanelManager,
    public state: ThreePointRegistrationPanelState,
    public viewer: Viewer,
  ) {
    super(sidePanelManager, state.location);
    this.addTitleBar({ title: "Point registration" });
    this.body.className = "neuroglancer-three-point-registration-body";
    this.addBody(this.body);
    this.buildUi();
    this.registerDisposer(
      registerActionListener(
        viewer.element,
        CAPTURE_ACTION,
        (event: ActionEvent<unknown>) => this.handleCaptureAction(event),
      ),
    );
    this.registerDisposer(
      viewer.layerManager.layersChanged.add(this.updateView),
    );
    this.registerDisposer(state.changed.add(this.updateView));
    this.registerDisposer(
      registerEventListener(
        document,
        "keydown",
        (event: Event) => this.handleKeyDown(event as KeyboardEvent),
        /*options=*/ true,
      ),
    );
    this.updateView();
  }

  private addLayerSelect(
    label: string,
    kind: PointKind,
    select: HTMLSelectElement,
  ) {
    const row = document.createElement("label");
    row.className = "neuroglancer-three-point-registration-row";
    row.appendChild(document.createTextNode(label));
    row.appendChild(select);
    select.addEventListener("change", () => {
      this.state.setLayerName(kind, select.value || undefined);
    });
    this.body.appendChild(row);
  }

  private addOutputLayerInput() {
    const row = document.createElement("label");
    row.className = "neuroglancer-three-point-registration-row";
    row.appendChild(document.createTextNode("Aligned layer"));
    this.outputLayerNameInput.type = "text";
    this.outputLayerNameInput.spellcheck = false;
    this.outputLayerNameInput.autocomplete = "off";
    this.outputLayerNameInput.addEventListener("change", () => {
      this.state.setOutputLayerName(this.outputLayerNameInput.value);
    });
    this.outputLayerNameInput.addEventListener("blur", () => {
      this.state.setOutputLayerName(this.outputLayerNameInput.value);
    });
    row.appendChild(this.outputLayerNameInput);
    this.body.appendChild(row);
  }

  private addSectionTitle(title: string) {
    const element = document.createElement("div");
    element.className = "neuroglancer-three-point-registration-section-title";
    element.textContent = title;
    this.body.appendChild(element);
  }

  private buildUi() {
    this.addLayerSelect("Fixed layer", "fixed", this.fixedSelect);
    this.addLayerSelect("Transform layer", "moving", this.movingSelect);
    this.addOutputLayerInput();

    const pointGrid = document.createElement("div");
    pointGrid.className = "neuroglancer-three-point-registration-points";
    this.body.appendChild(pointGrid);
    const indexHeader = document.createElement("div");
    pointGrid.appendChild(indexHeader);
    for (let index = 0; index < MAX_REGISTRATION_POINT_COUNT; ++index) {
      const header = document.createElement("div");
      header.className = "neuroglancer-three-point-registration-point-header";
      header.textContent = `P${index + 1}`;
      pointGrid.appendChild(header);
    }
    for (const kind of ["fixed", "moving"] as const) {
      const label = document.createElement("div");
      label.className = "neuroglancer-three-point-registration-point-index";
      label.textContent = kind === "fixed" ? "Fixed" : "Transform";
      pointGrid.appendChild(label);
      for (let index = 0; index < MAX_REGISTRATION_POINT_COUNT; ++index) {
        const cell = document.createElement("div");
        cell.className = "neuroglancer-three-point-registration-point-cell";
        const captureButton = document.createElement("button");
        captureButton.className =
          "neuroglancer-three-point-registration-point-status";
        captureButton.addEventListener("click", () =>
          this.togglePoint(kind, index),
        );
        this.pointCaptureButtons[kind][index] = captureButton;
        cell.appendChild(captureButton);
        pointGrid.appendChild(cell);
      }
    }

    const actions = document.createElement("div");
    actions.className = "neuroglancer-three-point-registration-actions";
    this.alignButton.textContent = "Align Layers";
    this.alignButton.addEventListener("click", () => this.alignLayers());
    actions.appendChild(this.alignButton);
    const scaleLabel = document.createElement("label");
    scaleLabel.className = "neuroglancer-three-point-registration-scale-toggle";
    scaleLabel.appendChild(document.createTextNode("Allow scale"));
    const scaleCheckbox = this.registerDisposer(
      new TrackableBooleanCheckbox(this.state.allowScale),
    );
    scaleLabel.appendChild(scaleCheckbox.element);
    actions.appendChild(scaleLabel);
    this.body.appendChild(actions);

    const copyControls = document.createElement("label");
    copyControls.className =
      "neuroglancer-three-point-registration-row neuroglancer-three-point-registration-copy-row";
    copyControls.appendChild(document.createTextNode("Copy transform"));
    const copyControlGroup = document.createElement("div");
    copyControlGroup.className =
      "neuroglancer-three-point-registration-copy-controls";
    for (const shape of transformCopyMatrixShapes) {
      const option = document.createElement("option");
      option.value = shape;
      option.textContent = shape;
      this.copyMatrixShapeSelect.appendChild(option);
    }
    this.copyMatrixShapeSelect.addEventListener("change", () => {
      this.state.setCopyMatrixShape(
        verifyTransformCopyMatrixShape(this.copyMatrixShapeSelect.value),
      );
    });
    copyControlGroup.appendChild(this.copyMatrixShapeSelect);
    for (const format of transformCopyFormats) {
      const option = document.createElement("option");
      option.value = format;
      option.textContent =
        format === "numpy"
          ? "NumPy"
          : format === "mango"
            ? "Mango XML"
            : format.toUpperCase();
      this.copyFormatSelect.appendChild(option);
    }
    this.copyFormatSelect.addEventListener("change", () => {
      this.state.setCopyFormat(
        verifyTransformCopyFormat(this.copyFormatSelect.value),
      );
    });
    copyControlGroup.appendChild(this.copyFormatSelect);
    copyControlGroup.appendChild(this.copyButton);
    copyControls.appendChild(copyControlGroup);
    this.body.appendChild(copyControls);

    this.addSectionTitle("Unaligned points");
    this.pointDetailsTable.className =
      "neuroglancer-three-point-registration-point-details";
    this.body.appendChild(this.pointDetailsTable);

    this.addSectionTitle("Aligned errors");
    this.errorTable.className =
      "neuroglancer-three-point-registration-error-table";
    this.body.appendChild(this.errorTable);
    this.messageElement.className =
      "neuroglancer-three-point-registration-message";
    this.body.appendChild(this.messageElement);
  }

  private updateLayerSelect(
    select: HTMLSelectElement,
    value: string | undefined,
  ) {
    const previous = select.value;
    select.textContent = "";
    const emptyOption = document.createElement("option");
    emptyOption.value = "";
    emptyOption.textContent = "";
    select.appendChild(emptyOption);
    for (const layer of this.viewer.layerManager.managedLayers) {
      const option = document.createElement("option");
      option.value = layer.name;
      option.textContent = layer.name;
      select.appendChild(option);
    }
    select.value = value ?? previous;
  }

  private getRegistrationLayout(
    movingTransform: CoordinateSpaceTransform,
  ): RegistrationLayout {
    const fixedLayer = getLayerByName(this.viewer, this.state.fixedLayerName);
    if (fixedLayer === undefined) throw new Error("Choose a fixed layer.");
    const fixedTransform = getFirstLoadedTransform(fixedLayer);
    if (fixedTransform === undefined) {
      throw new Error("Fixed layer transform is not ready.");
    }
    const globalSpace = this.viewer.coordinateSpace.value;
    const displayDimensions = this.viewer.displayDimensions.value;
    const displayedDimensionNames = Array.from(
      displayDimensions.displayDimensionIndices.slice(
        0,
        displayDimensions.displayRank,
      ),
      (index) => globalSpace.names[index],
    );
    const dimensionNames = getThreePointRegistrationDimensionNames(
      fixedTransform.outputSpace.names,
      movingTransform.outputSpace.names,
      displayedDimensionNames,
    );
    const dimensions = dimensionNames.map((name) => {
      const globalIndex = globalSpace.names.indexOf(name);
      if (globalIndex !== -1) {
        return {
          scale: globalSpace.scales[globalIndex] ?? 1,
          unit: globalSpace.units[globalIndex] ?? "",
        };
      }
      for (const transform of [fixedTransform, movingTransform]) {
        const index = transform.outputSpace.names.indexOf(name);
        if (index !== -1) {
          return {
            scale: transform.outputSpace.scales[index] ?? 1,
            unit: transform.outputSpace.units[index] ?? "",
          };
        }
      }
      return { scale: 1, unit: "" };
    }) as [PhysicalDimension, PhysicalDimension, PhysicalDimension];
    return {
      fixedLayer,
      dimensionNames,
      fixedOutputDimensionIndices: getOutputDimensionIndices(
        fixedTransform,
        dimensionNames,
      ),
      movingOutputDimensionIndices: getOutputDimensionIndices(
        movingTransform,
        dimensionNames,
      ),
      dimensions,
    };
  }

  private computeRegistration(): ComputedRegistration {
    const movingLayer = getLayerByName(this.viewer, this.state.movingLayerName);
    if (movingLayer === undefined) throw new Error("Choose a transform layer.");
    const movingTransform = getFirstLoadedTransform(movingLayer);
    if (movingTransform === undefined) {
      throw new Error("Transform layer transform is not ready.");
    }
    const layout = this.getRegistrationLayout(movingTransform);
    const pairs: RegistrationPointPair[] = [];
    let reachedUnusedOptionalPair = false;
    for (let i = 0; i < MAX_REGISTRATION_POINT_COUNT; ++i) {
      const moving = this.state.movingPoints[i]?.coordinates;
      const fixed = this.state.fixedPoints[i]?.coordinates;
      if (moving === undefined && fixed === undefined) {
        if (i < REQUIRED_REGISTRATION_POINT_COUNT) {
          throw new Error(
            "Capture the first three fixed and transform point pairs.",
          );
        }
        reachedUnusedOptionalPair = true;
        continue;
      }
      if (moving === undefined || fixed === undefined) {
        throw new Error(
          `Complete both points for P${i + 1}, or clear the pair.`,
        );
      }
      if (reachedUnusedOptionalPair) {
        throw new Error(`Complete earlier point pairs before using P${i + 1}.`);
      }
      pairs.push({ moving, fixed });
    }
    if (getUnitLabel(layout.dimensions) === "mixed units") {
      throw new Error(
        "Registration dimensions must use the same physical unit.",
      );
    }
    const coordinateScales = layout.dimensions.map(({ scale }) => scale);
    const allowScale = this.state.allowScale.value;
    const result = estimateThreePointRegistrationWithCoordinateScales(
      pairs,
      coordinateScales,
      allowScale,
    );
    // The reverse registration is obtained by re-fitting with the roles of the two point sets
    // swapped.  For rigid/similarity fits this is the least-squares inverse, and is better
    // conditioned than inverting the forward matrix.
    const reverseResult = estimateThreePointRegistrationWithCoordinateScales(
      pairs.map(({ moving, fixed }) => ({ moving: fixed, fixed: moving })),
      coordinateScales,
      allowScale,
    );
    return {
      movingLayer,
      fixedLayer: layout.fixedLayer,
      layout,
      pairs,
      result,
      reverseResult,
    };
  }

  private updatePointDetails() {
    this.pointDetailsTable.textContent = "";
    const movingLayer = getLayerByName(this.viewer, this.state.movingLayerName);
    const movingTransform =
      movingLayer === undefined
        ? undefined
        : getFirstLoadedTransform(movingLayer);
    let layout: RegistrationLayout | undefined;
    if (movingTransform !== undefined) {
      try {
        layout = this.getRegistrationLayout(movingTransform);
      } catch {
        layout = undefined;
      }
    }
    const dimensions: PhysicalDimension[] =
      layout === undefined
        ? [
            { scale: 1, unit: "" },
            { scale: 1, unit: "" },
            { scale: 1, unit: "" },
          ]
        : layout.dimensions;
    const unitLabel = getUnitLabel(dimensions);
    const mixedUnits = unitLabel === "mixed units";
    for (const text of ["Point", "Fixed physical", "Transform physical"]) {
      const header = document.createElement("div");
      header.className =
        "neuroglancer-three-point-registration-point-detail-header";
      header.textContent = text;
      this.pointDetailsTable.appendChild(header);
    }
    const fixedPhysicalPoints = this.state.fixedPoints.map(
      (point) => point?.coordinates,
    );
    const movingPhysicalPoints = this.state.movingPoints.map(
      (point) => point?.coordinates,
    );
    let displayedPointCount = REQUIRED_REGISTRATION_POINT_COUNT;
    for (
      let i = REQUIRED_REGISTRATION_POINT_COUNT;
      i < MAX_REGISTRATION_POINT_COUNT;
      ++i
    ) {
      if (
        this.state.fixedPoints[i] !== undefined ||
        this.state.movingPoints[i] !== undefined
      ) {
        displayedPointCount = i + 1;
      }
    }
    for (let i = 0; i < displayedPointCount; ++i) {
      const next = (i + 1) % displayedPointCount;
      const pointLabel = document.createElement("div");
      pointLabel.className =
        "neuroglancer-three-point-registration-point-detail-label";
      pointLabel.textContent = `P${i + 1}`;
      this.pointDetailsTable.appendChild(pointLabel);
      for (const point of [fixedPhysicalPoints[i], movingPhysicalPoints[i]]) {
        const value = document.createElement("div");
        value.className =
          "neuroglancer-three-point-registration-point-detail-value";
        value.textContent = formatPhysicalCoordinates(point, dimensions);
        this.pointDetailsTable.appendChild(value);
      }

      const distanceLabel = document.createElement("div");
      distanceLabel.className =
        "neuroglancer-three-point-registration-point-detail-label neuroglancer-three-point-registration-point-distance";
      distanceLabel.textContent = `P${i + 1}-P${next + 1}`;
      this.pointDetailsTable.appendChild(distanceLabel);
      for (const points of [fixedPhysicalPoints, movingPhysicalPoints]) {
        const value = document.createElement("div");
        value.className =
          "neuroglancer-three-point-registration-point-detail-value neuroglancer-three-point-registration-point-distance";
        value.textContent = formatDistance(
          distanceBetween(points[i], points[next], dimensions),
          unitLabel,
          mixedUnits,
        );
        this.pointDetailsTable.appendChild(value);
      }
    }
  }

  private updateView = () => {
    this.updateLayerSelect(this.fixedSelect, this.state.fixedLayerName);
    this.updateLayerSelect(this.movingSelect, this.state.movingLayerName);
    this.outputLayerNameInput.value = this.state.outputLayerName ?? "";
    this.copyFormatSelect.value = this.state.copyFormat;
    this.copyMatrixShapeSelect.value = this.state.copyMatrixShape;
    this.copyMatrixShapeSelect.disabled = this.state.copyFormat === "mango";
    for (const kind of ["fixed", "moving"] as const) {
      const points =
        kind === "fixed" ? this.state.fixedPoints : this.state.movingPoints;
      for (let i = 0; i < MAX_REGISTRATION_POINT_COUNT; ++i) {
        const pointIsSet = points[i] !== undefined;
        const captureButton = this.pointCaptureButtons[kind][i];
        const pointIsEnabled = this.state.isPointIndexEnabled(i);
        const isActive =
          this.activeCapture?.kind === kind && this.activeCapture.index === i;
        captureButton.textContent = pointIsSet ? "●" : "○";
        captureButton.disabled = !pointIsEnabled;
        captureButton.title = pointIsEnabled
          ? `${isActive ? "Cancel" : pointIsSet ? "Clear" : "Set"} ${
              kind === "moving" ? "transform" : "fixed"
            } point P${i + 1}`
          : `Complete point pair P${i} to enable P${i + 1}`;
        captureButton.classList.toggle(
          "neuroglancer-three-point-registration-point-set",
          pointIsSet,
        );
        captureButton.classList.toggle(
          "neuroglancer-three-point-registration-point-unset",
          !pointIsSet,
        );
        captureButton.classList.toggle(
          "neuroglancer-three-point-registration-active",
          isActive,
        );
      }
    }
    this.updatePointDetails();
    this.errorTable.textContent = "";
    let message = "";
    try {
      const computed = this.computeRegistration();
      const dimensions = computed.layout.dimensions;
      const unitLabel = getUnitLabel(dimensions);
      const mixedUnits = unitLabel === "mixed units";
      const physicalErrors = mixedUnits
        ? undefined
        : getPhysicalRegistrationErrors(
            computed.result,
            computed.pairs,
            dimensions,
          );
      const pointErrorLabel = document.createElement("div");
      pointErrorLabel.className =
        "neuroglancer-three-point-registration-point-detail-label";
      pointErrorLabel.textContent = `Points (${computed.pairs
        .map((_, i) => `P${i + 1}`)
        .join(", ")})`;
      const pointErrorValue = document.createElement("div");
      pointErrorValue.className =
        "neuroglancer-three-point-registration-point-detail-value neuroglancer-three-point-registration-point-distance";
      pointErrorValue.textContent = formatDistanceList(
        computed.pairs.map((_, i) =>
          mixedUnits
            ? computed.result.residuals[i]
            : physicalErrors!.residuals[i],
        ),
        unitLabel,
        mixedUnits,
      );
      this.errorTable.appendChild(pointErrorLabel);
      this.errorTable.appendChild(pointErrorValue);
      const rmsLabel = document.createElement("div");
      rmsLabel.className =
        "neuroglancer-three-point-registration-point-detail-label";
      rmsLabel.textContent = "RMS / max";
      const rmsValue = document.createElement("div");
      rmsValue.className =
        "neuroglancer-three-point-registration-point-detail-value neuroglancer-three-point-registration-point-distance";
      rmsValue.textContent = formatDistanceList(
        [
          mixedUnits ? computed.result.rmsError : physicalErrors!.rmsError,
          mixedUnits ? computed.result.maxError : physicalErrors!.maxError,
        ],
        unitLabel,
        mixedUnits,
      );
      this.errorTable.appendChild(rmsLabel);
      this.errorTable.appendChild(rmsValue);
      this.alignButton.disabled = false;
    } catch (error) {
      message = error instanceof Error ? error.message : `${error}`;
      this.alignButton.disabled = true;
    }
    if (this.state.outputLayerName === undefined) {
      message = "Choose an aligned layer name.";
      this.alignButton.disabled = true;
    }
    if (this.activeCapture !== undefined) {
      const kind = this.activeCapture.kind === "moving" ? "transform" : "fixed";
      message = `Click in a data view to capture ${kind} point ${
        this.activeCapture.index + 1
      }.`;
    }
    this.messageElement.textContent = message;
  };

  private togglePoint(kind: PointKind, index: number) {
    if (!this.state.isPointIndexEnabled(index)) return;
    if (
      this.activeCapture?.kind === kind &&
      this.activeCapture.index === index
    ) {
      this.cancelCapture();
      this.state.setPoint(kind, index, undefined);
      this.updateView();
      return;
    }
    this.armCapture(kind, index);
  }

  private armCapture(kind: PointKind, index: number) {
    this.cancelCapture();
    const map = new EventActionMap();
    map.set("at:mousedown0", {
      action: CAPTURE_ACTION,
      stopPropagation: true,
      preventDefault: true,
    });
    this.activeCapture = {
      kind,
      index,
      disposers: [
        this.viewer.inputEventBindings.sliceView.addParent(map, 100),
        this.viewer.inputEventBindings.perspectiveView.addParent(map, 100),
      ],
    };
    this.updateView();
  }

  private cancelCapture() {
    const activeCapture = this.activeCapture;
    if (activeCapture === undefined) return;
    for (const disposer of activeCapture.disposers) disposer();
    this.activeCapture = undefined;
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (event.code !== "Escape" || this.activeCapture === undefined) return;
    event.preventDefault();
    event.stopPropagation();
    this.cancelCapture();
    this.updateView();
  }

  private capturePoint(
    dimensionNames: readonly string[],
    outputDimensionIndices: readonly (number | undefined)[],
  ) {
    const coordinates = getMouseCoordinates(
      this.viewer,
      dimensionNames,
      "skip",
    );
    // A dimension the layer does not provide is represented by a synthetic
    // singleton axis spanning [0, 1), so the data lies at 0 along it. The
    // mouse position is reported in the global coordinate space, which may
    // well contain that dimension, and using its value would fit the
    // registration to a plane the data does not occupy: the resulting
    // transform would only be correct at that offset, leaving the layer
    // visibly misaligned once rendered at the singleton coordinate.
    for (let i = 0; i < 3; ++i) {
      if (outputDimensionIndices[i] !== undefined) continue;
      coordinates[i] = 0;
    }
    return { coordinates };
  }

  private handleCaptureAction(event: ActionEvent<unknown>) {
    const activeCapture = this.activeCapture;
    if (activeCapture === undefined) return;
    event.preventDefault();
    try {
      const movingLayer = getLayerByName(
        this.viewer,
        this.state.movingLayerName,
      );
      if (movingLayer === undefined)
        throw new Error("Choose a transform layer.");
      const movingTransform = getFirstLoadedTransform(movingLayer);
      if (movingTransform === undefined) {
        throw new Error("Transform layer transform is not ready.");
      }
      const layout = this.getRegistrationLayout(movingTransform);
      this.state.setPoint(
        activeCapture.kind,
        activeCapture.index,
        this.capturePoint(
          layout.dimensionNames,
          activeCapture.kind === "fixed"
            ? layout.fixedOutputDimensionIndices
            : layout.movingOutputDimensionIndices,
        ),
      );
      this.cancelCapture();
    } catch (error) {
      const message = new StatusMessage();
      message.setErrorMessage(
        error instanceof Error ? error.message : `${error}`,
      );
      this.cancelCapture();
    }
    this.updateView();
  }

  private navigateToFixedRegistrationPlane(layout: RegistrationLayout) {
    const globalSpace = this.viewer.coordinateSpace.value;
    const currentPosition = this.viewer.navigationState.position.value;
    const position = new Float32Array(currentPosition);
    let changed = false;
    for (let i = 0; i < 3; ++i) {
      if (layout.fixedOutputDimensionIndices[i] !== undefined) continue;
      const globalIndex = globalSpace.names.indexOf(layout.dimensionNames[i]);
      if (globalIndex === -1) continue;
      let coordinate = 0;
      let pointCount = 0;
      for (const point of this.state.fixedPoints) {
        if (point === undefined) continue;
        coordinate += point.coordinates[i];
        ++pointCount;
      }
      coordinate /= pointCount;
      if (position[globalIndex] !== coordinate) {
        position[globalIndex] = coordinate;
        changed = true;
      }
    }
    if (changed) this.viewer.navigationState.position.value = position;
  }

  private makeAlignedLayerJson(
    sourceLayer: ManagedUserLayer,
    sourceLayerDescription: string,
    layout: RegistrationLayout,
    result: RegistrationResult,
  ) {
    const userLayer = sourceLayer.layer;
    if (userLayer === null) {
      throw new Error(`${sourceLayerDescription} layer is not ready.`);
    }
    const layerJson = userLayer.toJSON();
    layerJson.source = userLayer.dataSources.map((dataSource) => {
      const spec = dataSource.spec;
      const loadState = dataSource.loadState;
      if (loadState === undefined || loadState.error !== undefined) {
        return layerDataSourceSpecificationToJson(spec);
      }
      const currentTransform = loadState.transform.value;
      if (
        getPresentDimensionCount(
          currentTransform.outputSpace.names,
          layout.dimensionNames,
        ) < 2
      ) {
        throw new Error(
          `Every ${sourceLayerDescription.toLowerCase()}-layer source must provide at least two registration dimensions.`,
        );
      }
      const alignedTransform = applyThreePointRegistrationResult(
        currentTransform,
        layout.dimensionNames,
        layout.dimensions,
        result,
      );
      return layerDataSourceSpecificationToJson({
        ...spec,
        transform: {
          sourceRank: alignedTransform.sourceRank,
          transform: alignedTransform.transform,
          outputSpace: alignedTransform.outputSpace,
          inputSpace: alignedTransform.inputSpace,
        },
      });
    });
    return layerJson;
  }

  /**
   * Creates the aligned layer named `name`, or re-initializes it in place if it already exists.
   *
   * New layers are placed alongside `counterpart`: they are added to exactly those layer groups
   * that contain the counterpart layer, immediately after it.  Existing layers are left where they
   * are, so re-running the alignment does not undo a manual rearrangement.
   */
  private createOrUpdateAlignedLayer(
    name: string,
    layerJson: any,
    counterpart: ManagedUserLayer,
  ) {
    const { layerSpecification } = this.viewer;
    const existingLayer = this.viewer.layerManager.getLayerByName(name);
    if (existingLayer !== undefined) {
      initializeLayerFromSpec(existingLayer, layerJson);
      return existingLayer;
    }
    const layer = new ManagedUserLayer(name, layerSpecification);
    initializeLayerFromSpec(layer, layerJson);
    // Add to the global spec.  This also covers the SingletonLayerGroupViewer case, where there
    // are no subsets at all.
    layerSpecification.add(layer);
    // Place the layer in each group that contains its counterpart, just after it.
    for (const subset of layerSpecification.subsets) {
      const { layerManager } = subset;
      if (!layerManager.has(counterpart)) continue;
      const counterpartIndex = layerManager.managedLayers.indexOf(counterpart);
      subset.add(
        layer.addRef(),
        counterpartIndex === -1 ? undefined : counterpartIndex + 1,
      );
    }
    return layer;
  }

  private alignLayers() {
    try {
      const computed = this.computeRegistration();
      const { movingLayer, fixedLayer, layout } = computed;
      const name = this.state.outputLayerName;
      if (name === undefined) throw new Error("Choose an aligned layer name.");
      if (name === movingLayer.name) {
        throw new Error("Aligned layer name must differ from transform layer.");
      }
      if (name === fixedLayer.name) {
        throw new Error("Aligned layer name must differ from fixed layer.");
      }
      const reverseName = getReverseOutputLayerName(fixedLayer.name);
      if (reverseName === undefined) {
        throw new Error("Choose a fixed layer.");
      }
      if (reverseName === movingLayer.name) {
        throw new Error(
          "Reverse-aligned layer name must differ from transform layer.",
        );
      }
      if (reverseName === fixedLayer.name) {
        throw new Error(
          "Reverse-aligned layer name must differ from fixed layer.",
        );
      }
      if (reverseName === name) {
        throw new Error(
          "Aligned and reverse-aligned layer names must differ.  Rename the aligned layer.",
        );
      }
      // Build both specifications up front so a failure in either direction aborts before any
      // layer is created.
      const layerJson = this.makeAlignedLayerJson(
        movingLayer,
        "Transform",
        layout,
        computed.result,
      );
      const reverseLayerJson = this.makeAlignedLayerJson(
        fixedLayer,
        "Fixed",
        layout,
        computed.reverseResult,
      );
      // The transform layer aligned into the fixed frame belongs beside the fixed layer, and vice
      // versa.
      const layer = this.createOrUpdateAlignedLayer(
        name,
        layerJson,
        fixedLayer,
      );
      this.createOrUpdateAlignedLayer(
        reverseName,
        reverseLayerJson,
        movingLayer,
      );
      this.viewer.selectedLayer.layer = layer;
      this.viewer.selectedLayer.visible = true;
      this.navigateToFixedRegistrationPlane(layout);
    } catch (error) {
      const message = new StatusMessage();
      message.setErrorMessage(
        error instanceof Error ? error.message : `${error}`,
      );
    }
  }

  private computeLayerTransformDifference(): LayerTransformDifference {
    const transformLayer = getLayerByName(
      this.viewer,
      this.state.movingLayerName,
    );
    if (transformLayer === undefined) {
      throw new Error("Choose a transform layer.");
    }
    const alignedLayerName = this.state.outputLayerName;
    if (alignedLayerName === undefined) {
      throw new Error("Choose an aligned layer name.");
    }
    const alignedLayer = getLayerByName(this.viewer, alignedLayerName);
    if (alignedLayer === undefined) {
      throw new Error(
        `Aligned layer ${JSON.stringify(alignedLayerName)} does not exist.`,
      );
    }
    const transformLayerTransform = getFirstLoadedTransform(transformLayer);
    if (transformLayerTransform === undefined) {
      throw new Error("Transform layer transform is not ready.");
    }
    const alignedLayerTransform = getFirstLoadedTransform(alignedLayer);
    if (alignedLayerTransform === undefined) {
      throw new Error("Aligned layer transform is not ready.");
    }
    const layout = this.getRegistrationLayout(transformLayerTransform);
    const difference = getThreePointRegistrationTransformDifference(
      transformLayerTransform,
      alignedLayerTransform,
      layout.dimensionNames,
      layout.dimensions,
    );
    const dimensionIndices = layout.dimensionNames.map((name) => {
      const index = alignedLayerTransform.outputSpace.names.indexOf(name);
      if (index === -1) throw new Error(`Missing spatial dimension ${name}.`);
      return index;
    }) as [number, number, number];
    return {
      transformLayer,
      alignedLayer,
      copyLayout: {
        dimensionIndices,
        dimensions: layout.dimensions,
      },
      ...difference,
    };
  }

  private async copyTransform() {
    try {
      const difference = this.computeLayerTransformDifference();
      await navigator.clipboard.writeText(
        formatAffineTransformForCopy(
          difference.transform,
          difference.rank,
          this.state.copyFormat,
          this.state.copyMatrixShape,
          difference.copyLayout,
        ),
      );
      const message = new StatusMessage();
      message.setText("Copied transform.");
    } catch (error) {
      const message = new StatusMessage();
      message.setErrorMessage(
        error instanceof Error ? error.message : `${error}`,
      );
    }
  }
}
